/**
 * Universal Mobile Number Keyboard Enforcer
 * 
 * Ensures that all numeric inputs throughout the entire website automatically
 * summon the native numeric keypad on iOS (Safari, WebKit) and Android (Chrome, etc.)
 * by default, eliminating standard alphanumeric keyboard popups for numbers.
 */

const DECIMAL_REGEX = /price|cost|rate|pct|percent|fee|discount|cogs|margin|spend|subtotal|tax|gst|ratio|scale|decimal|float|amount|weight|kg|g|commission|roi|roas|cac|ctr|cpc|cpm|aov/i;

export function enforceInputNumberKeyboard(input) {
  if (!input || input.tagName !== 'INPUT') return;

  const type = (input.getAttribute('type') || input.type || '').toLowerCase();
  const hasNumericPattern = input.getAttribute('pattern') === '[0-9]*';
  const hasNumericFlag = input.getAttribute('data-numeric') === 'true';

  // Only apply to numeric inputs
  if (type !== 'number' && !hasNumericPattern && !hasNumericFlag) {
    return;
  }

  // If developer explicitly marked it as custom, respect that
  if (input.hasAttribute('data-keyboard-custom')) {
    return;
  }

  const existingMode = input.getAttribute('inputmode');
  const step = input.getAttribute('step');
  const name = input.getAttribute('name') || input.name || '';
  const id = input.getAttribute('id') || input.id || '';
  const label = input.getAttribute('aria-label') || '';
  const placeholder = input.getAttribute('placeholder') || '';
  const combinedMeta = `${name} ${id} ${label} ${placeholder}`;

  const isDecimal =
    step === 'any' ||
    (step && step.includes('.')) ||
    DECIMAL_REGEX.test(combinedMeta);

  const targetMode = isDecimal ? 'decimal' : 'numeric';

  if (!existingMode || (existingMode !== 'numeric' && existingMode !== 'decimal' && existingMode !== 'tel')) {
    input.setAttribute('inputmode', targetMode);
  }

  if (targetMode === 'numeric' && !input.hasAttribute('pattern')) {
    input.setAttribute('pattern', '[0-9]*');
  }
}

export function scanAndEnforceAllInputs(root = document) {
  if (typeof document === 'undefined' || !root) return;

  try {
    const numberInputs = root.querySelectorAll
      ? root.querySelectorAll('input[type="number"], input[data-numeric="true"], input[pattern="[0-9]*"]')
      : [];

    for (let i = 0; i < numberInputs.length; i++) {
      enforceInputNumberKeyboard(numberInputs[i]);
    }
  } catch (err) {
    // Ignore any selector errors in unusual environments
  }
}

let isInitialized = false;

export function initNumberKeyboardEnforcer() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || isInitialized) {
    return;
  }
  isInitialized = true;

  // 1. Initial scan on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scanAndEnforceAllInputs(document));
  } else {
    scanAndEnforceAllInputs(document);
  }

  // 2. Scan again on full window load
  window.addEventListener('load', () => scanAndEnforceAllInputs(document));

  // 3. Capture-phase interaction listeners:
  // Runs synchronously before the mobile OS displays the soft keyboard
  const handleInteraction = (e) => {
    const target = e.target;
    if (target && target.tagName === 'INPUT') {
      enforceInputNumberKeyboard(target);
    }
  };

  document.addEventListener('touchstart', handleInteraction, { capture: true, passive: true });
  document.addEventListener('pointerdown', handleInteraction, { capture: true, passive: true });
  document.addEventListener('focusin', handleInteraction, { capture: true });

  // 4. MutationObserver to handle dynamic React renders, modals, routing, etc.
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];

        if (mutation.type === 'childList') {
          for (let j = 0; j < mutation.addedNodes.length; j++) {
            const node = mutation.addedNodes[j];
            if (node.nodeType === 1) { // Node.ELEMENT_NODE
              if (node.tagName === 'INPUT') {
                enforceInputNumberKeyboard(node);
              } else if (node.querySelectorAll) {
                scanAndEnforceAllInputs(node);
              }
            }
          }
        } else if (mutation.type === 'attributes') {
          if (mutation.target && mutation.target.tagName === 'INPUT') {
            enforceInputNumberKeyboard(mutation.target);
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['type', 'step', 'inputmode'],
    });
  }
}

// Auto-run if loaded directly in browser context
if (typeof window !== 'undefined') {
  initNumberKeyboardEnforcer();
}
